// packages/frontend/src/components/admin/NamedRegionEditDialog.tsx
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNamedRegionsStore } from '../../store/namedRegionsStore';
import type { NamedRegion } from '../../types/namedRegion';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN',
  'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV',
  'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN',
  'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

interface Props {
  region?: NamedRegion;
  onClose: () => void;
}

export function NamedRegionEditDialog({ region, onClose }: Props) {
  const { createRegion, updateRegion } = useNamedRegionsStore();
  const [stateCode, setStateCode] = useState(region?.stateCode ?? 'CA');
  const [name, setName] = useState(region?.name ?? '');
  const [zip3Text, setZip3Text] = useState(
    region?.zip3s
      .map((z) => z.zip3)
      .sort()
      .join('\n') ?? '',
  );
  const [saving, setSaving] = useState(false);

  // Escape key dismisses (only while not saving).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  function parseZip3s(): string[] {
    return zip3Text
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  function validZip3s(zip3s: string[]): { ok: true } | { ok: false; bad: string } {
    for (const z of zip3s) {
      if (!/^[0-9]{3}$/.test(z)) return { ok: false, bad: z };
    }
    return { ok: true };
  }

  async function handleSave() {
    const zip3s = parseZip3s();
    if (zip3s.length === 0) {
      toast.error('Add at least one ZIP-3 prefix');
      return;
    }
    const valid = validZip3s(zip3s);
    if (!valid.ok) {
      toast.error(`"${valid.bad}" isn't a 3-digit ZIP-3 prefix`);
      return;
    }
    if (!name.trim()) {
      toast.error('Name required');
      return;
    }

    setSaving(true);
    try {
      if (region) {
        await updateRegion(region.id, { name: name.trim(), zip3s });
        toast.success(`Updated ${name}`);
      } else {
        await createRegion({ stateCode, name: name.trim(), zip3s });
        toast.success(`Created ${name}`);
      }
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        (err as Error).message;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg max-w-md w-full mx-4 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            {region ? `Edit region: ${region.name}` : 'Create region'}
          </h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label htmlFor="region-state" className="label">State</label>
            <select
              id="region-state"
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              disabled={!!region || saving}
              className="input"
            >
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="region-name" className="label">Name</label>
            <input
              id="region-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. NorCal"
              className="input"
              disabled={saving}
            />
          </div>

          <div>
            <label htmlFor="region-zip3s" className="label">
              ZIP-3 prefixes (one per line, or comma-separated)
            </label>
            <textarea
              id="region-zip3s"
              value={zip3Text}
              onChange={(e) => setZip3Text(e.target.value)}
              placeholder={'940\n941\n945'}
              rows={6}
              className="input font-mono"
              disabled={saving}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              3 digits each. Each ZIP-3 can belong to at most one region per state.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
